<template>
  <div class="employer-match">
    <div class="employer-match__body">
      <!-- Header: compact eyebrow + title block. -->
      <div class="employer-match__header">
        <p class="employer-match__eyebrow">Make it go further</p>
        <h2 class="employer-match__title">Double Your Impact</h2>
      </div>

      <!-- Multiplier card: $X → $2X, now with tighter padding + sizing. -->
      <div class="employer-match__multiplier">
        <div class="employer-match__amount employer-match__amount--base">
          <span class="employer-match__amount-label">Your gift</span>
          <span class="employer-match__amount-value">${{ donationAmountForView }}</span>
        </div>
        <div class="employer-match__plus" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </div>
        <div class="employer-match__amount employer-match__amount--matched">
          <span class="employer-match__amount-label">With match</span>
          <span class="employer-match__amount-value">${{ donationAmountMatched }}</span>
        </div>
      </div>

      <!-- Short description replaces the redundant "Find your employer"
           label — the search input's placeholder carries that copy now.
           Thousands-of-companies framing reassures the user that it's
           worth the 10-second check even if they aren't sure. -->
      <p class="employer-match__description">
        Thousands of companies match employee donations, meaning your gift
        to Side-Out could go <strong>twice as far</strong>. It takes
        10 seconds to check.
      </p>

      <!-- Employer search. The DTD plugin injects its UI into
           #dd-company-name-input; we wrap it in a relative shell so the
           results listbox (positioned absolute via :deep CSS below)
           floats over the Continue button instead of pushing it off. -->
      <fieldset class="employer-match__search">
        <div class="employer-match__search-shell">
          <div id="dd-company-name-input"></div>
        </div>
      </fieldset>
    </div>

    <!-- Actions — primary row mirrors Step 4's pattern: BACK pill
         (left, intrinsic width, --lg sized to match CONTINUE) +
         CONTINUE button (right, flex-fills). Skip link sits as a
         centered tertiary affordance below the row. -->
    <div class="employer-match__actions">
      <div class="employer-match__primary-row">
        <button
          @click.prevent="back"
          class="dw-back-pill dw-back-pill--lg"
          type="button"
          aria-label="Back"
        >
          <svg class="dw-back-pill__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
          <span>Back</span>
        </button>
        <button
          @click.prevent="next"
          class="employer-match__continue"
        >
          Continue
        </button>
      </div>
      <button
        @click.prevent="skip"
        class="employer-match__skip"
      >
        Skip this step
      </button>
    </div>
  </div>
</template>

<script>
import BigNumber from 'bignumber.js'

export default {
  name: 'employer-match-form',

  data() {
    return {
      ddInitialized: false,
    }
  },

  mounted() {
    // Lazily initialise the DoubleTheDonation streamlined search input.
    // Guarded because `doublethedonation` is a global loaded by an external
    // script tag; on slow networks it may not be ready yet.
    this.$nextTick(() => {
      this.initDoubleTheDonation()
    })
  },

  beforeUnmount() {
    if (this._dtdObserver) {
      this._dtdObserver.disconnect()
      this._dtdObserver = null
    }
  },

  computed: {
    donationWidgetState() {
      return this.$store.getters.donationWidget
    },
    calculatedFee() {
      const fixedFee = '30' // cents
      const percentageFee = new BigNumber(0.022)
      const one = new BigNumber(1)
      const goal = new BigNumber(this.donationWidgetState.amount)
      const calculatedCharge = goal.plus(fixedFee).dividedBy(one.minus(percentageFee))
      return BigNumber(calculatedCharge.minus(goal))
    },
    calculatedTotal() {
      const amount = new BigNumber(this.donationWidgetState.amount)
      if (this.donationWidgetState.paidForTransactionFee) {
        return BigNumber(amount.plus(this.calculatedFee))
      }
      return amount
    },
    donationAmountForView() {
      return new BigNumber(this.calculatedTotal).dividedBy(100).toFormat(2)
    },
    donationAmountMatched() {
      return new BigNumber(this.calculatedTotal).times(2).dividedBy(100).toFormat(2)
    },
  },

  methods: {
    initDoubleTheDonation() {
      if (this.ddInitialized) return
      if (typeof doublethedonation !== 'undefined'
          && doublethedonation.plugin
          && typeof doublethedonation.plugin.load_streamlined_input === 'function') {
        try {
          doublethedonation.plugin.load_streamlined_input()
          this.ddInitialized = true
          // DTD writes its own placeholder. Force "Find your employer"
          // to match our label copy and save space at the narrow
          // widget width. Retry briefly in case DTD populates after
          // this tick.
          this.applyPlaceholder()
        } catch (e) {
          // DTD init can throw on dev environments where the script is
          // blocked; not fatal — the user can still Continue / Skip.
          console.warn('DoubleTheDonation init failed:', e)
        }
      } else {
        // Retry once after a short delay in case the external script is
        // still loading.
        setTimeout(() => {
          if (!this.ddInitialized && typeof doublethedonation !== 'undefined') {
            this.initDoubleTheDonation()
          }
        }, 600)
      }
    },
    applyPlaceholder() {
      // DTD's streamlined input renders asynchronously and can also
      // re-render the input later (e.g. after a user clears the
      // selection). A MutationObserver watches the mount point and
      // re-applies both "Find your employer" and the match-note
      // wrapping any time DOM under the mount changes.
      const desired = 'Find your employer'
      const mount = document.getElementById('dd-company-name-input')
      if (!mount) return

      const apply = () => {
        // Placeholder enforcement (survives DTD re-renders).
        const input = mount.querySelector('input')
        if (input && input.getAttribute('placeholder') !== desired) {
          input.setAttribute('placeholder', desired)
        }
        this.highlightMatchNote(mount)
      }

      const tryApply = (attempt) => {
        apply()
        if (!mount.querySelector('input') && attempt < 6) {
          setTimeout(() => tryApply(attempt + 1), 200)
        }
      }
      tryApply(0)

      // Observe the mount for subsequent DTD re-renders. The observer
      // callback itself will cause mutations (wrapping text nodes), so
      // the apply() function guards against re-wrapping the same text
      // via the existing-.dd-match-note check.
      if (this._dtdObserver) this._dtdObserver.disconnect()
      this._dtdObserver = new MutationObserver(apply)
      this._dtdObserver.observe(mount, { childList: true, subtree: true })
    },

    // Finds DTD's post-selection notes and wraps them in styled spans
    // so CSS can render them on-brand. Two distinct cases, different
    // visual treatments:
    //   • "COMPANY matches gifts over $X"   → .dd-match-note (pink pill)
    //   • "COMPANY does not have a Matching…" → .dd-no-match-note (neutral info)
    // DTD's class names differ across versions, so text-content
    // matching via TreeWalker is the most reliable tag mechanism.
    highlightMatchNote(mount) {
      if (!mount) return
      // Idempotent — once wrapped, skip (also prevents observer loop).
      if (mount.querySelector('.dd-match-note, .dd-no-match-note')) return

      // Pattern → target class. Order matters: check the negative
      // case first because "does not have a Matching Gift program"
      // could match a broader phrase.
      const patterns = [
        { test: /does\s+not\s+have\s+a\s+Matching\s+Gift/i, klass: 'dd-no-match-note', extract: /([^.]*does\s+not\s+have[^.]*)\.?/i },
        { test: /matches\s+gifts\s+over/i, klass: 'dd-match-note', extract: /([^.]*matches\s+gifts\s+over[^.]*)\.?/i },
      ]

      for (const pat of patterns) {
        const walker = document.createTreeWalker(
          mount,
          NodeFilter.SHOW_TEXT,
          { acceptNode: (n) => n.nodeValue && pat.test.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT }
        )
        const node = walker.nextNode()
        if (!node) continue

        const text = node.nodeValue
        const m = text.match(pat.extract)
        if (!m) continue

        const parent = node.parentNode
        if (!parent) continue

        const before = document.createTextNode(text.slice(0, m.index))
        const after  = document.createTextNode(text.slice(m.index + m[0].length))

        const span = document.createElement('span')
        span.className = pat.klass
        span.textContent = m[1].trim()

        parent.replaceChild(after, node)
        parent.insertBefore(span, after)
        parent.insertBefore(before, span)
        return // wrap only one — avoid duplicate tagging across multi-text-node containers
      }
    },
    next() {
      this.donationWidgetState.enterFromDirection = 'translate-x-full'
      this.donationWidgetState.leaveToDirection = '-translate-x-full'
      this.donationWidgetState.steps.employerMatch.isInView = false
      this.donationWidgetState.steps.employerMatch.isValid = true
      this.donationWidgetState.steps.contact.isInView = true
    },
    // Functionally identical to next() — the "skip" label just gives
    // users who don't have an employer-match a lower-commitment exit.
    skip() {
      this.next()
    },
    back() {
      this.donationWidgetState.enterFromDirection = '-translate-x-full'
      this.donationWidgetState.leaveToDirection = 'translate-x-full'
      this.donationWidgetState.steps.employerMatch.isInView = false
      this.donationWidgetState.steps.payment.isInView = true
    },
  },
}
</script>

<style scoped>
/* ─── Layout ──────────────────────────────────────────────────
   Tighter column that fits in the stepper-reduced viewport.
   Body pushes actions to the bottom via margin-auto; the search
   shell is position:relative so the results dropdown can float
   absolute over the Continue button area. */
.employer-match {
  padding: 0 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.employer-match__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 50px;
}

/* ─── Header ─────────────────────────────────────────────── */
.employer-match__header { text-align: center; }
.employer-match__eyebrow {
  font-family: 'Red Hat Display', sans-serif;
  font-size: 0.6875rem;     /* 11px */
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  /* Darker brand lime — base $green (#97D700, sass/_variables.scss:19)
     reads at ~1.6:1 contrast on white and was flagged by the client as
     too light. #5C8B00 keeps the same hue family but drops HSL L from
     42% → 27% for legible foreground use. Applied uniformly across the
     eyebrow, matched-card label/value, arrow, and inline emphasis. */
  color: #5C8B00;
  margin: 0;
  line-height: 1;
}
.employer-match__title {
  font-size: 1.375rem;      /* 22px (was 28px) */
  font-weight: 800;
  color: #111827;
  line-height: 1.12;
  letter-spacing: -0.01em;
  margin: 4px 0 0;
  font-family: 'Red Hat Display', system-ui, sans-serif;
}

/* ─── Multiplier card ──────────────────────────────────────
   Flat pale lime + brand-green border so the "Double Your
   Impact" card reads as the positive/growth surface. Client
   directive: no gradients on these modal surfaces. */
.employer-match__multiplier {
  display: flex;
  align-items: stretch;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  background: #F4FBCF;
  border: 1.5px solid #D4E68D;
  position: relative;
  overflow: hidden;
}
.employer-match__amount {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 4px;         /* was 8px */
  border-radius: 8px;
  background: #ffffff;
  min-width: 0;
}
.employer-match__amount--base    { border: 1px solid #E5E7EB; }
.employer-match__amount--matched {
  /* Darker brand lime border (#5C8B00) — pairs with the darker foreground
     text/values inside, ensures the "match" card reads as a strong
     positive/growth state instead of fading into the pale-lime BG. */
  border: 1px solid #5C8B00;
  position: relative;
  z-index: 1;
}
.employer-match__amount-label {
  font-size: 0.625rem;      /* 10px */
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6B7280;
}
.employer-match__amount--matched .employer-match__amount-label { color: #5C8B00; }
.employer-match__amount-value {
  font-family: 'Red Hat Display', sans-serif;
  font-size: 1.375rem;      /* 22px (was 28px) */
  font-weight: 700;
  color: #111827;
  line-height: 1;
  margin-top: 2px;
}
.employer-match__amount--matched .employer-match__amount-value {
  color: #5C8B00;
  font-size: 1.625rem;      /* 26px (was 32px) */
}
.employer-match__plus {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5C8B00;
  width: 1.5rem;
}

/* ─── Description ────────────────────────────────────────── */
.employer-match__description {
  margin: 0;
  padding: 0 2px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 0.9375rem;        /* 15px (was 13px) — explanatory body, bumped for mobile legibility (client 2026-05-29) */
  line-height: 1.5;
  color: #4B5563;
  text-align: center;
}
.employer-match__description strong {
  /* Inline "twice as far" — uses the same darker brand lime as the
     eyebrow above so the impact framing reads as a unified positive cue. */
  color: #5C8B00;
  font-weight: 700;
}

/* ─── Search ─────────────────────────────────────────────── */
.employer-match__search { margin: 0; padding: 0; border: 0; }

/* Shell is relative so the DTD-injected dropdown can float absolute. */
.employer-match__search-shell {
  position: relative;
  z-index: 2;
}

/* ─── Actions ──────────────────────────────────────────────
   Two-row stack: primary row (BACK pill + CONTINUE button,
   side-by-side, matching heights), tertiary "Skip this step"
   centered below. */
.employer-match__actions {
  margin-top: auto;
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.employer-match__primary-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 8px;
}
.employer-match__continue {
  flex: 1 1 auto;
  padding: 10px 20px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #ffffff;
  background: #DA1884;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    background-color 180ms;
}
.employer-match__continue:hover {
  transform: translateY(-1px);
  background: #BC0E65;
}
.employer-match__continue:active { transform: translateY(0) scale(0.98); }
.employer-match__skip {
  width: 100%;
  padding: 4px 0;
  background: transparent;
  border: none;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  transition: color 180ms;
}
.employer-match__skip:hover { color: #DA1884; }
/* Step 3 back-pill alignment is now handled by .employer-match__primary-row
   (BACK is left, CONTINUE flex-fills right). No standalone anchor rule. */

@media (prefers-reduced-motion: reduce) {
  .employer-match__continue { transition-duration: 80ms; }
}

/* ─── DTD plugin injected UI ─────────────────────────────────
   DoubleTheDonation injects the input + results list into
   #dd-company-name-input. We don't control the markup, so we
   catch every descendant with wide-scope deep selectors and
   style for the most common shapes (input, ul/li, div rows). */
:deep(#dd-company-name-input) {
  position: static;
  width: 100%;
}
:deep(#dd-company-name-input input),
:deep(#dd-company-name-input input[type="text"]) {
  width: 100%;
  padding: 10px 12px 10px 38px;   /* left padding for the magnifying glass */
  font-size: 14px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  color: #111827;
  background: #ffffff
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23DA1884' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'><circle cx='11' cy='11' r='8'/><path d='M21 21l-4.35-4.35'/></svg>")
    no-repeat 12px center;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.04);
  outline: none;
  transition:
    border-color 200ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow   200ms cubic-bezier(0.22, 1, 0.36, 1);
}
:deep(#dd-company-name-input input::placeholder) { color: #9CA3AF; }
:deep(#dd-company-name-input input:focus) {
  border-color: #F0A3CE;
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.15);
}

/* Results dropdown — float below the input so it doesn't push the
   Continue button off-screen. DTD uses different container elements
   across versions (ul, div, ol); we target them all. */
:deep(#dd-company-name-input > ul),
:deep(#dd-company-name-input > ol),
:deep(#dd-company-name-input > div:not(.hidden)):not(:has(input)):not(:has(> select)) {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 40;
  max-height: 220px;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 6px;
  background: #ffffff;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  box-shadow:
    0 20px 40px -12px rgba(29, 37, 73, 0.18),
    0 8px 20px -8px rgba(218, 24, 132, 0.15);
  animation: employer-match-dropdown-in 220ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

/* Result rows — match the Lovable activity feed row feel. */
:deep(#dd-company-name-input li),
:deep(#dd-company-name-input > div > div),
:deep(#dd-company-name-input .dd-result),
:deep(#dd-company-name-input [role="option"]) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #1F2937;
  cursor: pointer;
  transition: background-color 140ms ease, color 140ms ease;
}
/* !important overrides DTD's inline styles — the plugin ships with
   a dark hover background on both the row and the inner text span,
   so we need to override the row AND every descendant. Otherwise
   a black rectangle hugs just the text even when the row's pink. */
:deep(#dd-company-name-input li:hover),
:deep(#dd-company-name-input li:hover *),
:deep(#dd-company-name-input li[aria-selected="true"]),
:deep(#dd-company-name-input li[aria-selected="true"] *),
:deep(#dd-company-name-input [role="option"]:hover),
:deep(#dd-company-name-input [role="option"]:hover *),
:deep(#dd-company-name-input [role="option"][aria-selected="true"]),
:deep(#dd-company-name-input [role="option"][aria-selected="true"] *),
:deep(#dd-company-name-input .dd-result:hover),
:deep(#dd-company-name-input .dd-result:hover *) {
  background: transparent !important;
  background-color: transparent !important;
  color: #DA1884 !important;
}
/* Re-apply the pink row background at the row level so the
   transparent-child rule above doesn't also wipe the row tint. */
:deep(#dd-company-name-input li:hover),
:deep(#dd-company-name-input li[aria-selected="true"]),
:deep(#dd-company-name-input [role="option"]:hover),
:deep(#dd-company-name-input [role="option"][aria-selected="true"]),
:deep(#dd-company-name-input .dd-result:hover) {
  background: #FFF5FA !important;
  background-color: #FFF5FA !important;
}
:deep(#dd-company-name-input li + li),
:deep(#dd-company-name-input [role="option"] + [role="option"]) {
  margin-top: 2px;
}

/* Legacy select dropdown (older DTD versions). */
:deep(#dd-company-name-input select) {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: #ffffff;
}

/* ─── Post-selection confirmation ────────────────────────────
   Once a company is chosen, DTD replaces the input with a
   "Company selected: X. X matches gifts over $Y" strip and a
   "Select a different company" link. We don't know the exact
   class names across DTD versions, so these :deep selectors
   target the most common containers / text patterns and style
   everything inside the mount point by default when no input
   is present. */
/* Default post-selection wrapper: NEUTRAL gray-50 card with info ℹ
   badge. This is the safe default — looks clean even if JS hasn't
   yet classified the match state. The pink celebratory variant
   below kicks in only when a match IS confirmed. */
:deep(#dd-company-name-input:not(:has(input))) {
  display: block;
  padding: 12px 14px 12px 44px;                        /* left pad for badge */
  background: #F9FAFB;                                 /* gray-50 */
  border: 1px solid #E5E7EB;                           /* gray-200 */
  border-left: 3px solid #94A3B8;                      /* slate-400 */
  border-radius: 10px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: #374151;                                      /* gray-700 */
  position: relative;
  box-shadow: 0 2px 6px -3px rgba(29, 37, 73, 0.08);
}
:deep(#dd-company-name-input:not(:has(input))::before) {
  content: '';
  position: absolute;
  top: 14px;
  left: 14px;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background: #ffffff
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='%2364748B' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'><circle cx='12' cy='12' r='10'/><line x1='12' y1='16' x2='12' y2='12'/><line x1='12' y1='8' x2='12.01' y2='8'/></svg>")
    no-repeat center center;
  border: 1px solid #CBD5E1;                           /* slate-300 */
}

/* Style the DTD-injected <p class="dtd-company-selected-text"> directly
   so content reads cleanly even before JS tagging runs. */
:deep(#dd-company-name-input .dtd-company-selected-text) {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #374151;
}

/* Match confirmed → upgrade to pink celebratory wrapper. Uses :has()
   to detect .dd-match-note injected by highlightMatchNote(). */
:deep(#dd-company-name-input:not(:has(input)):has(.dd-match-note)) {
  background: linear-gradient(135deg, #FFF5FA 0%, #FCE7F3 100%);
  border: 1.5px solid #F6C5E0;
  border-left: 1.5px solid #F6C5E0;                    /* reset left accent */
  box-shadow: 0 4px 12px -6px rgba(218, 24, 132, 0.2);
  color: #1F2937;
}
:deep(#dd-company-name-input:not(:has(input)):has(.dd-match-note)::before) {
  width: 22px;
  height: 22px;
  top: 13px;
  left: 13px;
  background: linear-gradient(135deg, #DA1884 0%, #BC0E65 100%)
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><polyline points='20 6 9 17 4 12'/></svg>")
    no-repeat center center;
  border: none;
  box-shadow: 0 2px 6px -2px rgba(218, 24, 132, 0.45);
}

/* Highlight company names / match-amount text ONLY in the match
   variant — in the no-match case we want company name navy, not
   celebratory pink. DTD nests these text nodes inside span/strong/b
   across versions. */
:deep(#dd-company-name-input:not(:has(input)):has(.dd-match-note) b),
:deep(#dd-company-name-input:not(:has(input)):has(.dd-match-note) strong),
:deep(#dd-company-name-input:has(.dd-match-note) .dd-company-name),
:deep(#dd-company-name-input:has(.dd-match-note) .dd-matched),
:deep(#dd-company-name-input:has(.dd-match-note) [data-dd-matched]) {
  color: #DA1884;
  font-weight: 700;
}
/* Company name in neutral (no-match or default) wrapper: navy bold */
:deep(#dd-company-name-input:not(:has(input)):not(:has(.dd-match-note)) b),
:deep(#dd-company-name-input:not(:has(input)):not(:has(.dd-match-note)) strong) {
  color: #1F2937;
  font-weight: 700;
}

/* "X matches gifts over $Y" match-policy pill — dynamically wrapped
   by highlightMatchNote() with a <span class="dd-match-note">. Stands
   out as a white card with pink dashed border + dollar glyph so the
   user can't miss the match program details. */
:deep(#dd-company-name-input .dd-match-note) {
  display: block;
  margin: 10px 0 4px;
  padding: 10px 14px 10px 38px;
  position: relative;
  background: #ffffff;
  border: 1.5px dashed #DA1884;
  border-radius: 10px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
  letter-spacing: 0.01em;
  box-shadow: 0 2px 8px -4px rgba(218, 24, 132, 0.3);
}
:deep(#dd-company-name-input .dd-match-note::before) {
  content: '';
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #DA1884 0%, #BC0E65 100%)
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><line x1='12' y1='1' x2='12' y2='23'/><path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'/></svg>")
    no-repeat center center;
  box-shadow: 0 2px 6px -2px rgba(218, 24, 132, 0.5);
}

/* "X does not have a Matching Gift program" note — the non-celebratory
   counterpart of the match pill. Neutral visual: soft gray-50 card,
   slate left accent bar (not pink — we don't want to dress up a
   non-event), info glyph in muted color, calmer copy tone. Pairs
   with gentle reassurance microcopy below. */
:deep(#dd-company-name-input .dd-no-match-note) {
  display: block;
  margin: 10px 0 4px;
  padding: 10px 14px 10px 38px;
  position: relative;
  background: #F9FAFB;                           /* gray-50 */
  border: 1px solid #E5E7EB;                     /* gray-200 */
  border-left: 3px solid #94A3B8;                /* slate-400 accent */
  border-radius: 10px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #4B5563;                                /* gray-600 */
  line-height: 1.45;
}
:deep(#dd-company-name-input .dd-no-match-note::before) {
  content: '';
  position: absolute;
  top: 12px;
  left: 12px;
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  background: #ffffff
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='%2364748B' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'><circle cx='12' cy='12' r='10'/><line x1='12' y1='16' x2='12' y2='12'/><line x1='12' y1='8' x2='12.01' y2='8'/></svg>")
    no-repeat center center;
  border: 1px solid #CBD5E1;                     /* slate-300 */
}
/* Reassurance microcopy after the note — "your gift still matters". */
:deep(#dd-company-name-input .dd-no-match-note::after) {
  content: 'Your gift still goes to work. Continue when you\'re ready.';
  display: block;
  margin-top: 6px;
  padding-left: 0;
  font-size: 12px;
  font-weight: 500;
  color: #6B7280;
  font-style: italic;
}

/* "Select a different company" link / any anchor inside the mount. */
:deep(#dd-company-name-input a) {
  display: inline-block;
  margin-top: 6px;
  color: #6B7280;
  text-decoration: none;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-bottom: 1px solid #D1D5DB;
  padding-bottom: 1px;
  cursor: pointer;
  transition: color 160ms ease, border-color 160ms ease;
}
:deep(#dd-company-name-input a:hover) {
  color: #DA1884;
  border-color: #DA1884;
}
:deep(#dd-company-name-input a:focus-visible) {
  outline: 2px solid #DA1884;
  outline-offset: 2px;
  border-radius: 4px;
}

@keyframes employer-match-dropdown-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>

<!--
  Non-scoped block: DTD injects HTML directly into #dd-company-name-input
  from external JS. Those elements don't carry Vue's scoped data-v
  attribute, and Vue's :deep() escape hatch has edge cases with complex
  selectors like :has() / :not() combined with dynamically-injected DOM.
  Moving these rules into a global block sidesteps the scoping pipeline
  entirely — #dd-company-name-input is a unique page ID so there's no
  collision risk.
-->
<style>
/* Default post-selection wrapper: NEUTRAL gray-50 card with info
   badge. Safe default — looks clean even if the JS text-walker
   hasn't run yet. The pink celebratory variant (further below)
   kicks in only when the match IS positively identified. */
#dd-company-name-input:not(:has(input)) {
  display: block;
  padding: 12px 14px 12px 44px;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-left: 3px solid #94A3B8;
  border-radius: 10px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: #374151;
  position: relative;
  box-shadow: 0 2px 6px -3px rgba(29, 37, 73, 0.08);
  margin-top: 10px;
}
#dd-company-name-input:not(:has(input))::before {
  content: '';
  position: absolute;
  top: 14px;
  left: 14px;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background: #ffffff
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='%2364748B' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'><circle cx='12' cy='12' r='10'/><line x1='12' y1='16' x2='12' y2='12'/><line x1='12' y1='8' x2='12.01' y2='8'/></svg>")
    no-repeat center center;
  border: 1px solid #CBD5E1;
}

#dd-company-name-input .dtd-company-selected-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #374151;
}
#dd-company-name-input .dtd-company-selected-text b,
#dd-company-name-input .dtd-company-selected-text strong {
  color: #1F2937;
  font-weight: 700;
}

/* Wrapped match-policy pill (pink). Rendered by highlightMatchNote()
   wrapping the "X matches gifts over $Y" phrase in <span class="dd-match-note">. */
#dd-company-name-input .dd-match-note {
  display: block;
  margin: 10px 0 4px;
  padding: 10px 14px 10px 38px;
  position: relative;
  background: #ffffff;
  border: 1.5px dashed #DA1884;
  border-radius: 10px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
  box-shadow: 0 2px 8px -4px rgba(218, 24, 132, 0.3);
}
#dd-company-name-input .dd-match-note::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #DA1884 0%, #BC0E65 100%)
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><line x1='12' y1='1' x2='12' y2='23'/><path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'/></svg>")
    no-repeat center center;
  box-shadow: 0 2px 6px -2px rgba(218, 24, 132, 0.5);
}

/* Wrapped no-match note (neutral). Rendered by highlightMatchNote()
   wrapping "X does not have a Matching Gift program" in
   <span class="dd-no-match-note">. */
#dd-company-name-input .dd-no-match-note {
  display: block;
  margin: 10px 0 4px;
  padding: 10px 14px 10px 38px;
  position: relative;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-left: 3px solid #94A3B8;
  border-radius: 10px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #4B5563;
  line-height: 1.45;
}
#dd-company-name-input .dd-no-match-note::before {
  content: '';
  position: absolute;
  top: 12px;
  left: 12px;
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  background: #ffffff
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='%2364748B' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'><circle cx='12' cy='12' r='10'/><line x1='12' y1='16' x2='12' y2='12'/><line x1='12' y1='8' x2='12.01' y2='8'/></svg>")
    no-repeat center center;
  border: 1px solid #CBD5E1;
}

/* When a match IS confirmed via JS tagging, upgrade the outer
   wrapper to pink-celebratory. Uses :has() which is in all modern
   evergreen browsers. */
#dd-company-name-input:not(:has(input)):has(.dd-match-note) {
  background: linear-gradient(135deg, #FFF5FA 0%, #FCE7F3 100%);
  border: 1.5px solid #F6C5E0;
  border-left: 1.5px solid #F6C5E0;
  box-shadow: 0 4px 12px -6px rgba(218, 24, 132, 0.2);
  color: #1F2937;
}
#dd-company-name-input:not(:has(input)):has(.dd-match-note)::before {
  width: 22px;
  height: 22px;
  top: 13px;
  left: 13px;
  background: linear-gradient(135deg, #DA1884 0%, #BC0E65 100%)
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><polyline points='20 6 9 17 4 12'/></svg>")
    no-repeat center center;
  border: none;
  box-shadow: 0 2px 6px -2px rgba(218, 24, 132, 0.45);
}
#dd-company-name-input:has(.dd-match-note) .dtd-company-selected-text b,
#dd-company-name-input:has(.dd-match-note) .dtd-company-selected-text strong {
  color: #DA1884;
}

/* "Select a different company" link / any anchor inside the mount. */
#dd-company-name-input a {
  display: inline-block;
  margin-top: 6px;
  color: #6B7280;
  text-decoration: none;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-bottom: 1px solid #D1D5DB;
  padding-bottom: 1px;
  cursor: pointer;
  transition: color 160ms ease, border-color 160ms ease;
}
#dd-company-name-input a:hover {
  color: #DA1884;
  border-color: #DA1884;
}
</style>
